import * as React from 'react'
import { flexRender, type Table as TanstackTable } from '@tanstack/react-table'
import { cn } from '@/lib/utils'
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TablePagination } from '@/components/data-table/table-pagination'
import LoadingAnimate from '@/components/loading-animate'
import { isEmpty } from 'lodash'

interface DataTableProps<TData> {
  /**
   * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
   * @type TanstackTable<TData>
   */
  table: TanstackTable<TData>

  totalRecords?: number

  /**
   * The floating bar to render at the bottom of the table on row selection.
   * @default null
   * @type React.ReactNode | null
   * @example floatingBar={<TasksTableFloatingBar table={table} />}
   */
  floatingBar?: React.ReactNode | null

  /**
   * Whether the table is loading data.
   * @default false
   * @type boolean
   */
  isLoading?: boolean

  /**
   * Callback when a row is clicked.
   * @type (row: TData) => void
   */
  onClick?: (row: TData) => void

  /**
   * Disable the rows per page selector in pagination.
   * @default false
   * @type boolean
   */
  disableRowPerPage?: boolean

  /**
   * Disable pagination entirely.
   * @default false
   * @type boolean
   */
  disablePagination?: boolean

  emptyMessage: string
}

export function Table<TData>({
  table,
  floatingBar = null,
  onClick,
  isLoading = false,
  disableRowPerPage = false,
  totalRecords,
  disablePagination = false,
  emptyMessage,
}: DataTableProps<TData>) {
  return (
    <div className="max-w-screen">
      <div className="w-full space-y-2.5 rounded-xl border bg-background">
        <TableComponent>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers
                  .filter((item) => item.column.getIsVisible())
                  .map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  onClick={() => onClick?.(row.original)}
                  className={cn({
                    'cursor-pointer': !!onClick,
                  })}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {isLoading ? (
                    <div className="lg:h-[500px] flex items-center justify-center">
                      <LoadingAnimate iconClassName="w-16" />
                    </div>
                  ) : (
                    <>{emptyMessage}</>
                    // 'No users found. Try adding new users or adjusting your filters.'
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TableComponent>
      </div>
      {!disablePagination && (
        <div className="sticky inset-x-0 bottom-0 items-center bg-sidebar mt-6">
          <div className="relative flex flex-col gap-2.5">
            <TablePagination
              table={table}
              totalRecords={totalRecords}
              disableRowPerPage={disableRowPerPage}
              isLoading={isLoading}
            />
            {table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
          </div>
        </div>
      )}
    </div>
  )
}
